import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useState } from 'react'
import Modal from '../Modal'

interface Tag {
  id: number
  title?: string
  label?: string
  status?: string
}

interface InvoiceConfirmationModalProps {
  isOpen: boolean
  completedTags: Tag[]
  onClose: () => void
  onConfirm: () => void
}

const InvoiceConfirmationModal: React.FC<InvoiceConfirmationModalProps> = ({
  isOpen,
  completedTags,
  onClose,
  onConfirm,
}) => {
  const [submitting, setSubmitting] = useState(false)
  if (!isOpen) return null

  return (
    <Modal onClose={onClose}>
      <div className={css(styles.confirmationModal)}>
        <div className={css(styles.confirmationHeader)}>
          <div className={css(styles.confirmationHeaderIcon)}>
            <i className="fa-solid fa-file-invoice" />
          </div>
          <h2 className={css(styles.confirmationHeaderTitle)}>
            Generate Invoice
          </h2>
        </div>
        <div className={css(styles.confirmationContent)}>
          <p className={css(styles.confirmationText)}>
            Generating invoice from the following completed tags:
          </p>
          <div className={css(styles.tagsList)}>
            {completedTags.map((tag, index) => (
              <div key={tag.id || index} className={css(styles.tagItem)}>
                <div className={css(styles.tagInfo)}>
                  <span className={css(styles.tagTitle)}>
                    {tag.title || 'Untitled Tag'}
                  </span>
                  <span className={css(styles.tagLabel)}>{tag.label}</span>
                </div>
                <div className={css(styles.tagStatus)}>
                  <span className={css(styles.statusBadge, styles.complete)}>
                    {tag.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={css(styles.confirmationActions)}>
          <button onClick={onClose} className={css(styles.cancelButton)}>
            Cancel
          </button>
          <button
            onClick={async () => {
              if (submitting) return
              setSubmitting(true)
              try {
                await onConfirm()
              } finally {
                setSubmitting(false)
              }
            }}
            className={css(styles.proceedButton)}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" /> Generating...
              </>
            ) : (
              'Continue'
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}

const styles = StyleSheet.create({
  confirmationModal: {
    padding: '32px',
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    boxShadow:
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    margin: '0 auto',
  },
  confirmationHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '20px',
    color: '#1C64F2',
  },
  confirmationHeaderIcon: {
    fontSize: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    backgroundColor: '#EBF4FF',
    borderRadius: '50%',
  },
  confirmationHeaderTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  confirmationContent: {
    marginBottom: '32px',
  },
  confirmationText: {
    fontSize: '16px',
    color: '#4B5563',
    lineHeight: '1.6',
    marginBottom: '20px',
    textAlign: 'center',
  },
  tagsList: {
    maxHeight: '300px',
    overflowY: 'auto',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    backgroundColor: '#F9FAFB',
  },
  tagItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #E5E7EB',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  tagInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  tagTitle: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#111827',
  },
  tagLabel: {
    fontSize: '12px',
    color: '#6B7280',
  },
  tagStatus: {
    display: 'flex',
    alignItems: 'center',
  },
  statusBadge: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'uppercase',
  },
  complete: {
    backgroundColor: '#D1FAE5',
    color: '#065F46',
  },
  confirmationActions: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'space-between',
  },
  cancelButton: {
    padding: '12px 24px',
    backgroundColor: '#6B7280',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '120px',
    '&:hover': {
      backgroundColor: '#4B5563',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    },
  },
  proceedButton: {
    padding: '12px 24px',
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '120px',
    '&:hover': {
      backgroundColor: '#1955CC',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    },
  },
})

export default InvoiceConfirmationModal
